<template>
  <div class="conversation">
    <header>
      <div v-if="$store.state.websocket.loadingTicket" class="skeleton-message">
        <b-skeleton width="36px" height="36px" circle />
        <b-skeleton width="100px" />
      </div>
      <new-messenger-conversation-header :ticket="ticket" target="conversation" />
      <b-skeleton v-if="$store.state.websocket.loadingTicket" width="20%" position="is-right" />
      <div v-else class="conversation-header-actions">
        <div class="conversation-currently-reading" />
      </div>
    </header>
    <div
      ref="conversationMessages"
      class="conversation-content"
      :class="{ 'drag-over': isDragOver }"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div v-if="$store.state.websocket.loadingMessageMore" class="scroll-information">
        <b-loading active :is-full-page="false" />
      </div>
      <div v-if="$store.state.websocket.loadingTicket" class="skeleton-messages conversation-content-inner">
        <div class="skeleton-message">
          <b-skeleton width="36px" height="36px" circle />
          <b-skeleton width="60%" height="36px" />
        </div>
        <div class="skeleton-message-reversed">
          <b-skeleton width="36px" height="36px" circle />
          <b-skeleton width="60%" height="36px" position="is-right" />
        </div>
        <div class="skeleton-message">
          <b-skeleton width="36px" height="36px" circle />
          <b-skeleton width="60%" height="36px" />
        </div>
        <div class="skeleton-message-reversed">
          <b-skeleton width="36px" height="36px" circle />
          <b-skeleton width="60%" height="36px" position="is-right" />
        </div>
      </div>
      <div v-else ref="conversationMessages" class="conversation-content-inner" @scroll="checkScroll">
        <!-- TODO: BONUS: grupowanie wiadomości od tej samej osoby do max interwału czasowego np 5min  -->
        <new-messenger-conversation-item
          v-for="(item, index) in messages"
          :key="index"
          :item="item"
          :current="item.isMine"
        />
        <new-messenger-conversation-item v-if="isTyping" is-typing />
      </div>
      <div ref="bottomMarker" />
    </div>
    <div class="conversation-input">
      <new-messenger-conversation-input
        :upload-files="uploadFiles"
        :disabled-input="disabledInput"
        :ticket-id="ticket.id"
        @files="processFiles"
        @append="appendMessage"
        @delete-file="deleteFile"
      />
    </div>
  </div>
</template>

<script>
// import UserAvatar from '~/marketplace-front-core/components/shared/Avatar'
import NewMessengerConversationItem from '../NewMessengerConversationItem'
import NewMessengerConversationHeader from './NewMessengerConversationHeader'
import NewMessengerConversationInput from './NewMessengerConversationInput'
import sortArrayObjects from '~/marketplace-front-core/utils/sortArrayObjects'

export default {
  name: 'NewMessengerConversation',
  components: {
    // UserAvatar,
    NewMessengerConversationHeader,
    NewMessengerConversationItem,
    NewMessengerConversationInput
  },
  props: {
    ticket: {
      type: Object,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    },
    isTyping: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isDragOver: false,
      uploadFiles: [],
      uploadedFiles: [],
      loading: true,
      maxFilesSize: 30 * 1024 * 1024,
      disabledInput: false,
      limit: 4
    }
  },
  methods: {
    checkScroll ({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop === 0 && this.messages?.length) {
        this.$emit('loadMoreMessages', this.messages[0].id)
      }
    },
    scrollToBottom () {
      const conversationMessages = this.$refs.conversationMessages
      // const bottomMarker = this.$refs.bottomMarker
      conversationMessages.scrollTop = this.$refs.conversationMessages.scrollHeight
    },
    appendMessage (message) {
      const filter = this.uploadFiles.filter(x => x && x.res && x.res.id)
      const attachments = filter.map(x => x.res.id)

      if (!this.disabledInput) {
        const newMessage = {
          createdAt: new Date().toUTCString(),
          files: filter.map(x => x.res).sort(sortArrayObjects('id')),
          id: null,
          loading: true,
          isMine: true,
          message,
          sender: this.ticket && this.ticket.client ? this.ticket.client : null,
          type: 'message',
          updatedAt: new Date().toUTCString()
        }
        this.$emit('addMessage', newMessage)

        this.$store.dispatch('websocket/sendMessage', { message, attachments })
        this.uploadFiles = []
      }
    },
    handleDragOver (event) {
      event.preventDefault()
      this.isDragOver = true
    },
    handleDragLeave (event) {
      event.preventDefault()
      this.isDragOver = false
    },
    handleDrop (event) {
      event.preventDefault()
      this.isDragOver = false
      const droppedFiles = Array.from(event.dataTransfer.files)
      this.processFiles(droppedFiles)
    },
    processFiles (files) {
      // Kolejkowanie plików
      files.forEach((file, index) => {
        if (this.conditionals(file)) {
          this.disabledInput = true

          const generateID = Math.random().toString(36).substr(2)
          const uploadFileIndex = this.uploadFiles.push({
            UID: generateID,
            name: file.name,
            type: file.type,
            size: file.size,
            res: null,
            error: null,
            path: URL.createObjectURL(file),
            loading: true
          })
          const uploadFile = this.uploadFiles[uploadFileIndex - 1]

          const formData = new FormData()
          formData.append('file', file, file.name)
          formData.append('lang', null)

          this.$api.messenger.sendFiles(formData)
            .then((res) => {
              // TODO to trzeba ujednolicić z API
              res.image = res.sizes
              uploadFile.res = res
            })
            .catch((e) => {
              uploadFile.error = true
            })
            .finally(() => {
              uploadFile.loading = false

              const attachments = this.uploadFiles.map(file => file.res)
              if (!attachments.includes(null)) {
                this.disabledInput = false
              } else {
                this.disabledInput = true
              }
            })
        }
      })
    },
    conditionals (file) {
      if (this.uploadFiles.length + 1 > this.limit) {
        this.$buefy.toast.open({
          message: this.$t('newMessenger.tooManyFiles', { maxFiles: this.limit }),
          type: 'is-danger'
        })
        return false
      }

      if (this.isFileOnBlackList(file)) {
        this.$buefy.toast.open({
          message: this.$t('newMessenger.invalidFileType'),
          type: 'is-danger'
        })
        return false
      }

      if (file.size > this.maxFilesSize) {
        this.$buefy.toast.open({
          message: this.$t('newMessenger.fileIsTooLarge', { maxFilesSize: this.maxFilesSize / 1024 / 1024 }),
          type: 'is-danger'
        })
        return false
      }

      return true
    },
    isFileOnBlackList (file) {
      const blacklistedTypes = [
        'application/x-msdos-program', // exe
        'application/x-msdownload', // exe
        'application/bat', // bat
        'application/x-bat', // bat
        'application/cmd', // cmd
        'application/x-cmd', // cmd
        'text/vbscript', // vbs
        'application/javascript', // js
        'text/javascript', // js
        'application/java-archive', // jar
        'text/x-python-script', // py
        'application/x-php', // php
        'application/vnd.ms-excel.macroEnabled.12', // xlsm
        'application/vnd.ms-powerpoint', // ppt
        'application/vnd.ms-powerpoint.presentation.macroEnabled.12', // pptm
        'application/x-mspublisher', // pub
        'text/html', // html
        'text/css', // css
        'text/plain', // txt
        'application/x-sh', // sh
        'application/x-ruby', // rb
        'application/x-perl', // pl
        'application/x-msi', // msi
        'application/vnd.android.package-archive' // apk
      ]

      return blacklistedTypes.includes(file.type)
    },
    deleteFile (fileId) {
      this.uploadFiles = this.uploadFiles.filter(file => file.UID !== fileId)
    }
  }
}
</script>

<style lang="scss" scoped>
$messenger-border-color: #E7E8E7;
$messenger-border-color-drop: #48C78E;
$messenger-background-avatar-color: #eee;
$messenger-avatar-text-color: #4A4A4A;

.conversation {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: 64px;
    padding: 0 16px;
    background-color: white;
    border-bottom: 1px solid $messenger-border-color;

    .conversation-header-actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: 8px;

      .conversation-currently-reading {
        display: flex;
        padding-left: 12px;
        padding-right: 8px;

        :deep(.avatar-container) {
          margin-left: -12px;
        }
      }
    }
  }

  .conversation-content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    height: 200px;
    // scroll-behavior: smooth;
  }
  .conversation-content-inner {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 16px;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    height: 200px;
    padding: 16px;
  }
  .conversation-content.drag-over {
    border: 2px dashed $messenger-border-color-drop;
  }
}

.conversation-input {
  border-top: 1px solid $messenger-border-color;
}

.plus-avatar {
  position: relative;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid white;
  background-color: $messenger-background-avatar-color;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -12px;
  // font-weight: bold;
}

.plus-icon {
  color: $messenger-avatar-text-color;
  font-size: 12px;
}

.skeleton-messages {
  // padding: 0 !important;
}

.skeleton-message, .skeleton-message-reversed {
  display: flex;
  align-items: center;
  gap: 10px;
  & > .b-skeleton:first-child {
    width: 36px;
  }
  .b-skeleton {
    margin-top: 0;
  }
}
.skeleton-message-reversed {
  flex-direction: row-reverse;
}
.scroll-information {
  position: sticky;
  left: calc(50% - 24px);
  right: 0;
  top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background-color: white;
  z-index: 100;
  box-shadow: 0 2px 2px rgba($text_color, 0.2);
  :deep(.loading-icon):after {
    width: 2rem;
    height: 2em;
    top: calc(50% - 1em);
    left: calc(50% - 1em);
  }
}
</style>
